<script setup lang="ts">
import {computed, ref, watch} from "vue";
import LegacyAccess from "@/core/interfaces/Access";
import {TeamAbsence} from "@/core/interfaces/TeamPlanning";
import {AbsenceStoreRequest} from "@/core/http/resources/TeamPlanningResource";
import useTeamAbsences from "@/planning/composables/useTeamAbsences";
import AbsenceRow from "@/planning/components/AbsenceRow.vue";
import AbsencePager from "@/planning/components/AbsencePager.vue";
import AbsenceFormDialog from "@/planning/components/AbsenceFormDialog.vue";
import AbsenceTimeline from "@/planning/components/AbsenceTimeline.vue";
import BaseFlexSpinner from "@/core/components/base/spinner/BaseFlexSpinner.vue";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle
} from "@/core/components/ui/alert-dialog";
import {formatAbsenceRange} from "@/planning/lib/absences";
import {apiErrorMessage, isAuthorizationError} from "@/core/http/apiErrorMessage";
import {useToastStore} from "@/stores/toast.store";
import {CalendarRange, History, List, Plus, TriangleAlert} from "@lucide/vue";

const {members, refreshPlanning, initialTab = 'list'} = defineProps<{
    /** The pharmacy team, to name and color the rows. */
    members: LegacyAccess[];
    /** Refetches the planning range, which any absence write makes stale. */
    refreshPlanning: () => Promise<void>;
    /** Tab the panel opens on, so a caller can point straight at the timeline. */
    initialTab?: AbsenceTab;
}>();

/** Rows per page of the active list, paged in the browser: the date filter already bounds it. */
const LIST_PAGE_SIZE = 10;

const TABS = [
    {key: 'list', label: 'Liste', icon: List, empty: 'Aucune absence en cours ni à venir.'},
    {key: 'calendar', label: 'Calendrier', icon: CalendarRange, empty: 'Aucune absence sur ce mois.'},
    {key: 'archive', label: 'Historique', icon: History, empty: 'Aucune absence archivée.'}
] as const;

export type AbsenceTab = typeof TABS[number]['key'];

const tab = ref<AbsenceTab>(initialTab);

const {
    active,
    activeLoading,
    activeFailed,
    activeLoaded,
    refreshActive,
    archiveLoading,
    archiveFailed,
    archiveLoaded,
    archiveTotal,
    archive,
    archivePage,
    archiveLastPage,
    goToArchivePage,
    refreshArchive,
    month,
    monthAnchor,
    monthLoading,
    monthFailed,
    monthLoaded,
    goToMonth,
    refreshMonth,
    create,
    update,
    remove
} = useTeamAbsences(refreshPlanning);

const {showSuccess, showError} = useToastStore();

const memberById = computed<Map<number, LegacyAccess>>(() =>
    new Map(members.map(member => [member.id, member])));

/** Each tab reads its own set, and answers for its own loading and failure. */
const shown = computed(() => {
    switch (tab.value) {
        case 'archive':
            return {
                loading: archiveLoading.value,
                failed: archiveFailed.value,
                loaded: archiveLoaded.value,
                count: archiveTotal.value,
                refresh: refreshArchive
            };
        case 'calendar':
            return {
                loading: monthLoading.value,
                failed: monthFailed.value,
                loaded: monthLoaded.value,
                count: month.value.length,
                refresh: refreshMonth
            };
        default:
            return {
                loading: activeLoading.value,
                failed: activeFailed.value,
                loaded: activeLoaded.value,
                count: active.value.length,
                refresh: refreshActive
            };
    }
});

const emptyMessage = computed<string>(() => TABS.find(({key}) => key === tab.value)!.empty);

/**
 * A failure before any success leaves nothing on screen, which the reader
 * must not confuse with a period that genuinely holds no absence.
 */
const failureMessage = computed<string | null>(() => {
    if (!shown.value.failed) {
        return null;
    }

    return shown.value.loaded
        ? 'Le dernier rafraîchissement a échoué : cette liste peut être incomplète.'
        : 'Les absences n\'ont pas pu être chargées.';
});

// ----- Active list -----

const listPage = ref<number>(1);

const listLastPage = computed<number>(() => Math.max(Math.ceil(active.value.length / LIST_PAGE_SIZE), 1));

const listRows = computed<TeamAbsence[]>(() =>
    active.value.slice((listPage.value - 1) * LIST_PAGE_SIZE, listPage.value * LIST_PAGE_SIZE));

// Emptying the last page by deleting its rows would otherwise leave the
// reader on a page that no longer exists.
watch(listLastPage, lastPage => {
    if (listPage.value > lastPage) {
        listPage.value = lastPage;
    }
});

// ----- Creation and edition -----

const formOpen = ref<boolean>(false);
const editedAbsence = ref<TeamAbsence | null>(null);
const saving = ref<boolean>(false);

const openCreate = () => {
    editedAbsence.value = null;
    formOpen.value = true;
};

const openEdit = (absence: TeamAbsence) => {
    editedAbsence.value = absence;
    formOpen.value = true;
};

const save = async (payload: AbsenceStoreRequest) => {
    const edited = editedAbsence.value;

    saving.value = true;

    try {
        if (edited) {
            // The member is immutable server-side, so it never travels.
            await update(edited.id, {
                starts_at: payload.starts_at,
                ends_at: payload.ends_at,
                reason: payload.reason
            });
            showSuccess({content: 'L\'absence a été modifiée'});
        } else {
            await create(payload);
            showSuccess({content: 'L\'absence a été enregistrée'});
        }

        formOpen.value = false;
        editedAbsence.value = null;
    } catch (error) {
        // The dialog stays open on failure: the typed period is not worth losing.
        if (!isAuthorizationError(error)) {
            showError({
                content: apiErrorMessage(
                    error,
                    edited ? 'L\'absence n\'a pas pu être modifiée' : 'L\'absence n\'a pas pu être enregistrée'
                )
            });
        }
    } finally {
        saving.value = false;
    }
};

// ----- Deletion -----

const absenceToDelete = ref<TeamAbsence | null>(null);
const deleteDialogOpen = ref<boolean>(false);
const deleting = ref<boolean>(false);

const requestDelete = (absence: TeamAbsence) => {
    absenceToDelete.value = absence;
    deleteDialogOpen.value = true;
};

/**
 * The target is read before anything else: AlertDialogAction closes the
 * dialog before the click handler runs.
 */
const confirmDelete = async () => {
    const absence = absenceToDelete.value;

    if (!absence) {
        return;
    }

    deleting.value = true;

    try {
        await remove(absence.id);
        showSuccess({content: 'L\'absence a été supprimée'});
    } catch (error) {
        if (!isAuthorizationError(error)) {
            showError({content: apiErrorMessage(error, 'L\'absence n\'a pas pu être supprimée')});
        }
    } finally {
        deleting.value = false;
        deleteDialogOpen.value = false;
        absenceToDelete.value = null;
    }
};

const retrying = ref<boolean>(false);

const retry = async () => {
    retrying.value = true;

    try {
        await shown.value.refresh();
    } finally {
        retrying.value = false;
    }
};
</script>

<template>
  <div class="rounded-lg border border-gray-200 bg-white p-4 shadow-custom">
    <div class="mb-3 flex flex-wrap items-center gap-2.5">
      <div class="flex-1 text-base font-semibold text-gray-800">
        Absences &amp; demandes
      </div>

      <div class="flex rounded-lg bg-gray-100 p-0.5">
        <button
          v-for="option in TABS"
          :key="option.key"
          class="flex h-7 items-center gap-1.5 rounded-md px-2.5 text-xs font-semibold transition-colors"
          :class="tab === option.key ? 'bg-white text-primary shadow-sm' : 'text-gray-500 hover:text-gray-700'"
          @click="tab = option.key"
        >
          <component :is="option.icon" class="size-3.5" />
          {{ option.label }}
          <span
            v-if="option.key === 'archive' && archiveTotal"
            class="rounded-full bg-gray-200 px-1.5 text-[9px] font-bold text-gray-600"
          >
            {{ archiveTotal }}
          </span>
        </button>
      </div>

      <button
        type="button"
        class="flex h-7 items-center gap-1.5 rounded-lg bg-primary px-3 text-xs font-semibold text-white transition-colors hover:bg-primary-dark"
        @click="openCreate"
      >
        <Plus class="size-3.5" />
        Ajouter une absence
      </button>
    </div>

    <div
      v-if="failureMessage"
      class="mb-2.5 flex flex-wrap items-center gap-2.5 rounded-lg border border-amber-200 bg-amber-50 px-3.5 py-2"
    >
      <TriangleAlert class="size-4 shrink-0 text-amber-700" />
      <span class="flex-1 text-[11.5px] font-medium text-amber-900">
        {{ failureMessage }}
      </span>
      <button
        class="h-7 rounded-lg border border-solid border-amber-300 bg-white px-2.5 text-[10.5px] font-semibold text-amber-900 hover:border-amber-500 disabled:cursor-not-allowed disabled:opacity-60"
        :disabled="retrying"
        @click="retry"
      >
        {{ retrying ? 'Nouvelle tentative…' : 'Réessayer' }}
      </button>
    </div>

    <!-- Only a first load blanks the panel: a refresh keeps what it has, so
         the banner above never claims a stale list while a spinner hides it. -->
    <BaseFlexSpinner v-if="shown.loading && !shown.loaded" />
    <AbsenceTimeline
      v-else-if="tab === 'calendar'"
      :anchor="monthAnchor"
      :members="members"
      :absences="month"
      @go="goToMonth"
      @select="openEdit"
    />
    <div
      v-else-if="!shown.count"
      class="rounded-lg border border-dashed border-gray-200 px-3 py-6 text-center text-xs text-gray-400"
    >
      {{ emptyMessage }}
    </div>
    <template v-else-if="tab === 'list'">
      <div class="flex flex-col gap-2">
        <AbsenceRow
          v-for="absence in listRows"
          :key="absence.id"
          :absence="absence"
          :member="memberById.get(absence.user_access_point_id)"
          editable
          deletable
          @edit="openEdit(absence)"
          @delete="requestDelete(absence)"
        />
      </div>
      <AbsencePager
        :page="listPage"
        :last-page="listLastPage"
        :total="active.length"
        noun="absence"
        @go="page => listPage = page"
      />
    </template>
    <!-- The past carries no actions: an archived absence is read, not amended. -->
    <template v-else-if="tab === 'archive'">
      <div class="flex flex-col gap-2">
        <AbsenceRow
          v-for="absence in archive"
          :key="absence.id"
          :absence="absence"
          :member="memberById.get(absence.user_access_point_id)"
        />
      </div>
      <AbsencePager
        :page="archivePage"
        :last-page="archiveLastPage"
        :total="archiveTotal"
        noun="absence archivée"
        plural="absences archivées"
        @go="goToArchivePage"
      />
    </template>

    <AbsenceFormDialog
      v-model:open="formOpen"
      :members="members"
      :absence="editedAbsence"
      :saving="saving"
      @submit="save"
    />

    <AlertDialog v-model:open="deleteDialogOpen">
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Supprimer cette absence ?</AlertDialogTitle>
          <AlertDialogDescription>
            {{ absenceToDelete?.reason }} ·
            {{ absenceToDelete ? formatAbsenceRange(absenceToDelete) : '' }}.
            Les horaires habituels du collaborateur seront rétablis sur la période.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Annuler</AlertDialogCancel>
          <AlertDialogAction :disabled="deleting" @click="confirmDelete">
            Supprimer
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  </div>
</template>
